/**
 * Serbian overrides for the analytics screens.
 *
 * Bosnian moved from ijekavica to ekavica — *uspjela* to *uspela*, *mjesec*
 * to *mesec*, *prijedlog* to *predlog*. Where the two standards agree there
 * is no entry, which is why this file is short and should stay that way.
 *
 * Latin script, matching the rest of the application.
 *
 * This screen carries more of the reflex than most, because the words it is
 * built out of are the ones that move: *rješavanje*, *prosjek*, *ocjena*,
 * *vrijeme*, *prijedlog*, *mjesec*, *smjena*, *procijenjen*. The two easy ones
 * to miss are *gdje* / *ovdje* and *dvije*, which carry no obvious stem.
 */

import type { AnalyticsSlice } from '../bs/analytics'

export const analytics: Partial<AnalyticsSlice> = {
  'analytics.subtitle': 'Pregled prijava, rešavanja i 5S rezultata',

  'analytics.range.months3': 'Zadnja 3 meseca',
  'analytics.range.months12': 'Zadnjih 12 meseci',

  'analytics.kpi.resolved': 'Rešeno',
  'analytics.kpi.avgResolution': 'Prosečno rešavanje',
  'analytics.kpi.fiveS': '5S prosek',
  'analytics.kpi.fiveS.hint': 'poslednja ocena po centru',
  'analytics.kpi.improvements': 'Predlozi',
  'analytics.kpi.acceptance': 'Prihvaćenost predloga',

  'analytics.series.resolved': 'Rešeno',
  'analytics.series.proposals': 'Predloga',

  'analytics.overTime.title': 'Prijave kroz vreme',
  'analytics.timeInStatus.title': 'Prosečno vreme po statusu',
  'analytics.timeInStatus.hint':
    'Pokazuje gde se prijave zadržavaju — najduži stubić je usko grlo procesa.',
  'analytics.timeInStatus.explain':
    'Vreme se broji tek kada prijava izađe iz statusa, pa ono što u njemu još stoji ne ulazi ni u jedan stubić.',
  'analytics.timeInStatus.empty': 'Još nema promena statusa',
  'analytics.timeInStatus.empty.description':
    'Podaci se pojavljuju kada prijave počnu da menjaju status.',
  'analytics.timeInStatus.tooltip': 'Prosek',

  'analytics.process.resolution': 'Do rešenja',
  'analytics.process.resolution.hint': 'Od prijave do statusa „rešeno”',
  'analytics.process.accepted': 'Prihvaćeno predloga',

  // *Sedmica* is not the word here; a week is *nedelju dana*.
  'analytics.backlog.hint':
    '„42 otvorene” ne govori ništa. Koliko ih je starije od nedelju dana — govori.',
  'analytics.backlog.explain':
    'Meri se od dana prijave do danas i obuhvata sve otvorene prijave, a ne samo one iz izabranog perioda.',

  'analytics.funnel.title': 'Put predloga za poboljšanje',
  'analytics.funnel.hint':
    'Od predloženog do sprovedenog — i koliko je od procenjene uštede zaista ostvareno.',
  'analytics.funnel.empty': 'Nema predloga u ovom periodu',
  'analytics.funnel.estimatedSavings': 'Procenjena ušteda',
  'analytics.funnel.estimatedCost': 'Procenjeni trošak',

  'analytics.rhythm.hint':
    'Prati smene. Smena koja ne prijavljuje ništa ili nema kvarova — ili ne prijavljuje.',

  'analytics.assignees.hint':
    'Deset otvorenih od kojih nijedna nije kasna je zdravo. Tri, od kojih su dve kasne, nije.',

  'analytics.fiveS.missing.title': 'Radni centri bez ocene ovog meseca',
  'analytics.fiveS.missing.hint':
    'Visok prosek na delu centara nije dobar mesec — ocenite i preostale.',
  'analytics.fiveS.trend.hint':
    'Prikazano u procentima. Montaža se ocenjuje 1–3, ostali centri 1–5, pa sirovi proseci nisu uporedivi.',
  'analytics.fiveS.byWorker.hint':
    'Prosek radnih centara koje je radnik održavao, po mesecu. Ocenjuje se centar, ne radnik — neko ko je preuzeo zapušten centar imaće nizak procenat upravo zato što je poslat da ga sredi.',
  'analytics.fiveS.byWorker.empty.description':
    'Radnici se biraju pri unosu 5S evaluacije. Evaluacije bez upisanih radnika se ovde ne prikazuju.',

  'analytics.fiveS.byCategory.title': 'Prosek po kategoriji',
  'analytics.fiveS.byCategory.explain':
    'Prosek svih odgovora u kategoriji za izabranu godinu, preračunat u procenat skale obrasca — Montaža se ocenjuje 1–3, ostali centri 1–5, pa sirove ocene ne stoje na istoj osi.',
  'analytics.fiveS.worst.title': 'Kriterijumi sa najslabijom ocenom',
  'analytics.fiveS.worst.explain':
    'Poređano po proseku za celu godinu, od najnižeg — kriterijum ocenjen jednom stoji ovde pored onog ocenjenog pedeset puta.',
  'analytics.fiveS.worst.average': 'Prosek',

  'analytics.pager.next': 'Sledeća stranica',

  // *smjena* -> *smena*, *ocjena* -> *ocena*, *prosjek* -> *prosek*,
  // *ovdje* -> *ovde*, *gdje* -> *gde*, *namjerno* -> *namerno*.
  'analytics.shifts.title': 'Po smenama',
  'analytics.shifts.subtitle':
    'Da li je bitno ko je na smeni. Ako sve smene drže mašinu jednako, problem je u mašini; ako je razlika velika, problem je u smeni.',
  'analytics.shifts.explain':
    'Sve ovde se gradi iz obilazaka na kojima je izabrana smena; dok smene ne postoje ili ih obilasci ne nose, nema se šta prikazati.',
  'analytics.shifts.unassigned.one':
    '{count} evaluacija nije vezana ni za jednu smenu, pa nije uključena u prikaz iznad.',
  'analytics.shifts.unassigned.few':
    '{count} evaluacije nisu vezane ni za jednu smenu, pa nisu uključene u prikaz iznad.',
  'analytics.shifts.unassigned.many':
    '{count} evaluacija nije vezano ni za jednu smenu, pa nisu uključene u prikaz iznad.',
  'analytics.shifts.spread.title': 'Gde je razlika među smenama najveća',
  'analytics.shifts.spread.hint':
    'Mašine koje jedna smena drži bitno bolje od druge. Prosek to skriva — ovde se vidi.',
  'analytics.shifts.spread.explain':
    'Razlika je između proseka najbolje i najslabije smene na tom centru, a centar ulazi u spisak tek kada su ga u periodu obišle najmanje dve smene.',
  'analytics.shifts.trend.title': 'Kretanje po smenama',
  'analytics.shifts.categories.title': 'Šta koja smena najslabije drži',
  'analytics.shifts.categories.hint':
    'Ocene su preračunate u procente, pa se Montaža (skala 1–3) poredi sa ostalim centrima (1–5).',
  'analytics.workforce.hint':
    'Tri odvojene stvari, namerno bez zbira. 5S govori o mašinama koje je neko držao, a ne o njemu; prijave govore o inicijativi; zadaci i o tome koliko je posla nekome dodeljeno. Jedan zbirni broj bi se čitao kao ocena radnika.',
  'analytics.workforce.rating.note':
    'Prihvaćeni predlozi se dodaju na osnovu i mogu je samo podići — odbijen predlog ne oduzima ništa. Ko nije bio ni na jednom obilasku ili nema nijedan zadatak, ocenjuje se samo po onome što ima, a deo koji nedostaje se ne računa kao nula.',
  'analytics.workforce.fiveS.hint':
    'Prosek svih obilazaka na kojima je radnik upisan; manji broj pored njega je razlika u odnosu na prethodni period iste dužine.',
  'analytics.workforce.walks.hint':
    'Broj predatih 5S obilazaka na kojima je radnik upisan; ispod {walks}, ocena nosi oznaku da počiva na malo podataka.',
  'analytics.workforce.accepted.hint':
    'U ocenu ulazi samo ovaj broj — koliko je prijava poslato i koliko je bodova osvojeno ne menja je.',
  'analytics.workforce.search.hint':
    'U tabeli su svi aktivni zaposleni, i oni bez ijednog podatka u periodu, da bi se i tihi mogli primetiti.',
  'analytics.workforce.role.hint':
    'Uloga ne menja način računanja ocene — svi se računaju po istoj formuli, pa ovaj izbor samo sužava spisak.',
  // *prije* -> *pre*, and the rank stays plant-wide in every language.
  'analytics.workforce.workCenter.hint':
    'Bira ljude raspoređene na taj centar; redni broj pored ocene ostaje mesto u celom pogonu, jer se rang računa pre filtera.',
  // *mjesec* -> *mesec*, *cijela* -> *cela*.
  'analytics.shifts.month': 'Mesec',
  'analytics.shifts.wholeYear': 'Cela godina',
  'analytics.shifts.none.title': 'Smene još nisu definisane',
  'analytics.shifts.none.description':
    'Kada dodate smene, evaluator ih bira pri 5S obilasku, a ovde se pojavljuje poređenje po smenama — koja smena drži koju mašinu bolje.',
  'analytics.shifts.none.action': 'Dodaj smene',
  'analytics.shifts.unused.title': 'Nijedna evaluacija još nije vezana za smenu',
  'analytics.shifts.unused.description':
    'Smene su definisane. Poređenje se pojavljuje nakon prvog 5S obilaska na kojem je izabrana smena — ranije evaluacije ostaju bez nje.',
  'analytics.workforce.ratedOnly': 'Samo ocenjeni',
  'analytics.workforce.ratedOnly.hint':
    'Sakrij one za koje nema dovoljno podataka za ocenu.',
}
